/**
 * Settings → Enrollment → Pre-baked installer (default tab).
 *
 * Lets an admin pick a tag, build a signed installer that already
 * embeds the tenant+tag identity, and copy MDM-ready distribution
 * snippets. The single-use enrollment-code flow lives in the
 * "Advanced" tab as a fallback.
 *
 * Spec: docs/backend-requirements/edr-sensor-installer-api.md
 */
import { useMemo, useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInstallers } from "@/hooks/security/useEdrInstallers";
import { BuildInstallerDialog } from "./BuildInstallerDialog";
import { InstallerDetailDrawer } from "./InstallerDetailDrawer";
import { InstallersTable } from "./InstallersTable";
import type { InstallerBuild, InstallerSummary } from "@/types/edr-installers";

export function InstallersPanel() {
  const listQ = useInstallers();
  const [buildOpen, setBuildOpen] = useState(false);
  const [activeBuild, setActiveBuild] = useState<InstallerBuild | null>(null);

  const result = listQ.data;
  const unavailable = result?.kind === "unavailable";
  const rows: InstallerSummary[] = result?.kind === "ok" ? result.rows : [];

  const subtitle = useMemo(() => {
    if (unavailable) return "Backend endpoint not provisioned yet";
    return `${rows.length} installer${rows.length === 1 ? "" : "s"}`;
  }, [unavailable, rows.length]);

  const handleSelectFromTable = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    // Promote the list summary to a "build" shape so the drawer renders.
    // downloadUrl / sha256 / size aren't on the summary — drawer shows a
    // "Refresh download URL" CTA when the URL is missing or expired.
    setActiveBuild({
      ...row,
      downloadUrl: "",
      downloadUrlExpiresAt: new Date(0).toISOString(),
      sha256: "—",
      size: 0,
    });
  };

  return (
    <>
      <Panel
        title="Sensor installers"
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => listQ.refetch()}
              disabled={listQ.isFetching}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", listQ.isFetching && "animate-spin")} />
            </Button>
            <Button size="sm" onClick={() => setBuildOpen(true)} disabled={unavailable}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Build installer
            </Button>
          </div>
        }
        loading={listQ.isLoading}
        isError={listQ.isError}
        error={listQ.error}
        onRetry={() => listQ.refetch()}
        empty={!listQ.isLoading && !listQ.isError && (unavailable || rows.length === 0)}
        emptyTitle={unavailable ? "Installer API not yet available" : "No installers yet"}
        emptyDescription={
          unavailable
            ? "Once the EDR installer endpoints ship, build tag-scoped, MDM-ready packages here. Use the Advanced tab to mint a single-use enrollment code in the meantime."
            : "Build your first installer — pick a tag, choose a platform, and we'll generate a signed package with the tenant and tag identity already embedded."
        }
        skeleton="table"
      >
        <InstallersTable rows={rows} onSelect={handleSelectFromTable} />
      </Panel>

      <BuildInstallerDialog
        open={buildOpen}
        onOpenChange={setBuildOpen}
        onBuilt={(b) => setActiveBuild(b)}
      />

      <InstallerDetailDrawer
        build={activeBuild}
        onOpenChange={(o) => !o && setActiveBuild(null)}
        onUpdate={(b) => setActiveBuild(b)}
      />
    </>
  );
}
